import { useHistory } from "react-router";

import { Button, Link, Text, toastError, toastSuccess } from "@alpacahq/ui/old";

import AmplitudeProvider from "~/AmplitudeProvider";
import {
  getErrorMessage,
  useGetIsLegalEntity,
  useGetOwners,
  useMutationRequest,
} from "~/v2/api/hooks";
import { useGetHYCAprTierOffer } from "~/v2/api/hooks/useGetHighYieldCash";
import { patchAccount, patchEsign } from "~/v2/api/rest/account";
import { postAgreementForLegalEntityAndSigner } from "~/v2/api/rest/entityOnboarding";
import { useLocalStorage } from "~/v2/hooks/useLocalStorage";
import { Path } from "~/v2/path";
import { useAccountContext } from "~/v2/providers/AccountProvider";
import { CUSTOMER_AGREEMENT_URL } from "../../../entityOnboarding/constants";
import { CUSTOMER_AGREEMENT_REVISION } from "../../../onboarding/constants";
import {
  getHighYieldCashLocalStorageKey,
  getIsEligibleForHighYieldCash,
} from "../../util";
import { FDIC_DEPOSIT_BANK_LIST_URL } from "../HighYieldCashFAQ";
import { Layout } from "../Layout";

const HighYieldCashEnrollmentAgreementPage = () => {
  const history = useHistory();

  const { product, currentAccount } = useAccountContext({ liveOnly: true });

  const { isLegalEntity, isLegalEntityLoading } = useGetIsLegalEntity();

  const [_, setHasAppliedForHighYieldCash] = useLocalStorage<string>(
    getHighYieldCashLocalStorageKey(currentAccount?.id),
  );

  const isEligible = getIsEligibleForHighYieldCash({
    product,
    currency: currentAccount?.currency,
    accountStatus: currentAccount?.status,
  });

  const { hycTierName, isLoading: isHYCTierNameLoading } =
    useGetHYCAprTierOffer({
      accountID: currentAccount?.id,
      options: { enabled: !!currentAccount?.id },
    });

  const { owners, isLoading: isOwnersLoading } = useGetOwners({
    accountId: currentAccount?.id ?? "",
    options: {
      enabled: !!currentAccount?.id && isLegalEntity && !isLegalEntityLoading,
    },
  });

  const legalEntityOwner = owners?.find((details) => details.is_legal_entity);

  const accountOpener = owners?.find(
    (details) => details.is_legal_account_opener,
  );

  const accountMutation = useMutationRequest(
    () =>
      patchAccount(currentAccount?.id ?? "", {
        cash_interest: [
          {
            currency: "USD",
            apr_tier_name: hycTierName,
          },
        ],
      }),
    {
      onSuccess: () => {
        setHasAppliedForHighYieldCash("true");
        toastSuccess(
          "You have successfully submitted a request to enroll in the High-Yield Cash program.",
        );
        history.push(Path.ROUTE_HIGH_YIELD_ENROLL_COMPLETED);
        AmplitudeProvider.dispatch("algodash_enrolled_high_yield_cash");
      },
      onError: (error) => {
        const errorMessage = getErrorMessage(error);
        toastError(`Error updating cash interest: ${errorMessage}`);
      },
    },
  );

  const customerAgreementMutation = useMutationRequest(
    async () =>
      isLegalEntity && accountOpener
        ? postAgreementForLegalEntityAndSigner({
            ownerId: legalEntityOwner?.owner_id ?? "",
            agreement: "customer_agreement",
            accountId: currentAccount?.id ?? "",
            signerPartyId: accountOpener?.owner_id ?? "",
            revision: CUSTOMER_AGREEMENT_REVISION,
          })
        : patchEsign(currentAccount?.id ?? "", {
            agreement: "customer_agreement",
            revision: CUSTOMER_AGREEMENT_REVISION,
            signed_at: new Date(),
          }),
    {
      onSuccess: () => {
        accountMutation.mutate(true);
      },
      onError: () => {
        toastError("Failed to update the customer agreement");
      },
    },
  );

  const onCancelClicked = () => {
    history.push(Path.ROUTE_DASHBOARD);
  };

  const onAgreeClicked = () => {
    if (isEligible) {
      customerAgreementMutation.mutate(true);
    }
  };

  return (
    <Layout
      title="Customer Agreement"
      isLoading={
        isHYCTierNameLoading || isLegalEntityLoading || isOwnersLoading
      }
    >
      <div className="rounded-lg bg-gray-100">
        <iframe
          id="hyc-customer-agreement-iframe"
          title="Alpaca Customer Agreement"
          height="568px"
          width="100%"
          src={CUSTOMER_AGREEMENT_URL}
          className="rounded-lg bg-gray-100"
        />
      </div>

      <Text className="text-gray-800 text-sm">
        By clicking on "I Agree" below, you are digitally signing the updated
        Alpaca Customer Agreement (“Customer Agreement”) above, and represents
        that you have read, understood, and agree to be bound by the Alpaca
        Securities LLC account terms, and all other terms, disclosures, and
        disclaimers applicable to you, as referenced in the Customer Agreement.
        <br />
        <br />
        Not all account types are eligible to participate in this program. For
        eligibility criteria, please refer to the{" "}
        <Link href={FDIC_DEPOSIT_BANK_LIST_URL}></Link>Alpaca FDIC Bank Sweep
        Program Terms and Conditions ("Terms & Conditions").
      </Text>

      <div className="flex items-center justify-between gap-4">
        <Button
          variant="alternate"
          className="border-yellow-400"
          onClick={onCancelClicked}
        >
          Cancel
        </Button>
        <Button
          onClick={onAgreeClicked}
          loading={
            accountMutation.isPending || customerAgreementMutation.isPending
          }
          disabled={
            !isEligible ||
            accountMutation.isPending ||
            customerAgreementMutation.isPending
          }
        >
          {!isEligible ? "Ineligible" : "I Agree"}
        </Button>
      </div>
    </Layout>
  );
};

export default HighYieldCashEnrollmentAgreementPage;
